import { assert } from "@greeter/assert";
import { change, ChangeState, createChangeState } from "@greeter/changes";
import { GreeterEvent, GreeterEventSchema, ImageAsset } from "@greeter/core";
import { IVenueApi } from "@greeter/venue-api";
import { notImplemented } from "@greeter/xstate-util";
import { assertEvent, assign, emit, raise, setup } from "xstate";
import * as z from "zod";

export interface EventEditorApiRequirements
  extends Pick<
    IVenueApi,
    | "fetchGreeterEvent"
    | "createGreeterEvent"
    | "updateGreeterEvent"
    | "deleteGreeterEvent"
    | "uploadAsset"
  > {}

export type EventChange =
  | { type: "setName"; data: string }
  | { type: "setDescription"; data: string }
  | { type: "setDressCode"; data: string }
  | { type: "setStartsAt"; data: Date }
  | { type: "setEndsAt"; data: Date }
  | { type: "setCoverAsset"; data: ImageAsset };

const EventFormSchema = GreeterEventSchema.refine(
  (e) => {
    return e.startsAt < e.endsAt;
  },
  {
    message: "Startstidspunktet burde være før sluttidspunktet",
    path: ["startsAt"],
  }
).refine((e) => e.coverAsset.id !== "" && e.coverAsset.id !== undefined, {
  message: "Du skal uploade et billede",
  path: ["coverAsset"],
});

type EventForm = z.infer<typeof EventFormSchema>;

function apply(f: EventForm, c: EventChange) {
  switch (c.type) {
    case "setName": {
      f.name = c.data;
      break;
    }
    case "setDescription": {
      f.description = c.data;
      break;
    }
    case "setStartsAt": {
      f.startsAt = c.data;
      break;
    }
    case "setEndsAt": {
      f.endsAt = c.data;
      break;
    }
    case "setDressCode": {
      f.dressCode = c.data;
      break;
    }
    case "setCoverAsset": {
      f.coverAsset = c.data;
      break;
    }
  }
}

type EventEditorEvents =
  | { type: "change"; data: EventChange }
  | { type: "fetch" }
  | { type: "create" }
  | { type: "delete" }
  | { type: "update" }
  | { type: "initialize" }
  | { type: "uploadAsset"; data: File };

type EventEditorEmitted =
  | { type: "failedToFetch"; error: Error }
  | { type: "failedToCreate"; error: Error }
  | { type: "failedToUpdate"; error: Error }
  | { type: "failedToDelete"; error: Error }
  | { type: "failedToUploadAsset"; error: Error }
  | { type: "created"; data: GreeterEvent }
  | { type: "updated"; data: GreeterEvent }
  | { type: "deleted" };

type EventEditorContext = {
  greeterEventId?: string;
  state: ChangeState<EventForm, EventChange>;
  validationErrors?: z.inferFlattenedErrors<typeof EventFormSchema>;
};

export const EventEditorMachine = setup({
  types: {} as {
    context: EventEditorContext;
    events: EventEditorEvents;
    input: { greeterEventId?: string };
    emitted: EventEditorEmitted;
  },
  actors: {
    fetchGreeterEvent: notImplemented<IVenueApi["fetchGreeterEvent"]>(),
    createGreeterEvent: notImplemented<IVenueApi["createGreeterEvent"]>(),
    updateGreeterEvent: notImplemented<IVenueApi["updateGreeterEvent"]>(),
    deleteGreeterEvent: notImplemented<IVenueApi["deleteGreeterEvent"]>(),
    uploadAsset: notImplemented<IVenueApi["uploadAsset"]>(),
  },
}).createMachine({
  type: "parallel",
  context: ({ input }) => ({
    state: createChangeState(GreeterEvent.empty()),
    greeterEventId: input.greeterEventId,
  }),
  states: {
    bg: {
      type: "parallel",
      states: {
        cover: {
          initial: "idle",
          states: {
            idle: {
              on: {
                uploadAsset: {
                  target: "uploading",
                  guard({ event }) {
                    return !!event.data;
                  },
                },
              },
            },
            uploading: {
              invoke: {
                src: "uploadAsset",
                input({ event }) {
                  assertEvent(event, "uploadAsset");
                  return [event];
                },
                onDone: {
                  actions: raise(({ event }) => ({
                    type: "change",
                    data: {
                      type: "setCoverAsset",
                      data: event.output,
                    },
                  })),
                },
              },
            },
          },
        },
        event: {
          initial: "uninitialized",
          states: {
            uninitialized: {
              entry: raise({ type: "initialize" }),
              on: {
                initialize: [
                  {
                    target: "fetching",
                    guard({ context }) {
                      return !!context.greeterEventId;
                    },
                  },
                  { target: "idle" },
                ],
              },
            },
            idle: {
              on: {
                fetch: "fetching",
                create: "creating",
                update: "updating",
                delete: "deleting",
              },
            },
            fetching: {
              invoke: {
                src: "fetchGreeterEvent",
                input({ context }) {
                  assert(
                    context.greeterEventId,
                    "context.greeterEventId should be something at this point"
                  );
                  return [context.greeterEventId];
                },
                onDone: {
                  target: "idle",
                  actions: assign(({ event }) => {
                    return { state: createChangeState(event.output, []) };
                  }),
                },
                onError: {
                  actions: emit(({ event }) => ({
                    type: "failedToFetch",
                    error: event.error as Error,
                  })),
                },
              },
            },
            creating: {
              invoke: {
                src: "createGreeterEvent",
                input({ context }) {
                  return [
                    {
                      ...context.state.value,
                      coverId: context.state.value.coverAsset.id,
                    },
                  ];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign({
                      state: ({ event }) => createChangeState(event.output),
                    }),
                    emit(({ event }) => ({
                      type: "created",
                      data: event.output,
                    })),
                  ],
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => ({
                    type: "failedToCreate",
                    error: event.error as Error,
                  })),
                },
              },
            },
            updating: {
              invoke: {
                src: "updateGreeterEvent",
                input({ context }) {
                  return [
                    {
                      ...context.state.value,
                      coverId: context.state.value.coverAsset.id,
                    },
                  ];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    assign({
                      state: ({ event }) => createChangeState(event.output),
                    }),
                    emit(({ event }) => ({
                      type: "updated",
                      data: event.output,
                    })),
                  ],
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => ({
                    type: "failedToUpdate",
                    error: event.error as Error,
                  })),
                },
              },
            },
            deleting: {
              invoke: {
                src: "deleteGreeterEvent",
                input({ context }) {
                  assert(
                    context.greeterEventId,
                    "context.greeterEventId is required to delete"
                  );
                  return [context.greeterEventId];
                },
                onDone: {
                  target: "idle",
                  actions: [
                    emit({
                      type: "deleted",
                    }),
                  ],
                },
                onError: {
                  target: "idle",
                  actions: emit(({ event }) => ({
                    type: "failedToDelete",
                    error: event.error as Error,
                  })),
                },
              },
            },
          },
        },
      },
    },
    fg: {
      entry: assign(({ context }) => ({
        validationErrors: EventFormSchema.safeParse(
          context.state.value
        ).error?.flatten(),
      })),
      on: {
        change: {
          actions: assign(({ context, event }) => {
            const newState = change(context.state, apply, event.data);
            const validationErrors = EventFormSchema.safeParse(newState.value);
            return {
              state: { ...newState },
              validationErrors: validationErrors.error?.flatten(),
            };
          }),
        },
      },
    },
  },
});
